// pages/PasswordRecovery/RecoverySetPasswordPage.tsx
import React from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button, Input, Label } from 'shared-portal-components-v2';

import { useRecoverySetPasswordForm } from '@/hooks/auth/useRecoverySetPasswordForm';
import PasswordRequirements from '@/components/molecules/PasswordRequirements/PasswordRequirements';
import SetPasswordError from '@/components/molecules/SetPasswordError/SetPasswordError';

export default function RecoverySetPasswordPage() {
  const { state, handlers } = useRecoverySetPasswordForm();

  return (
    <>
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">Set a new password</h1>
        <p className="text-muted-foreground text-lg">
          Your password must be at least 12 characters with uppercase, lowercase, number, and
          special character.
        </p>
      </div>

      <form className="flex flex-col gap-6" onSubmit={handlers.handleSubmit}>
        <SetPasswordError stage={state.stage} message={state.error} mode="recover" />

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="recovery-password" isRequired className="text-base">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="recovery-password"
                type={state.showPassword ? 'text' : 'password'}
                required
                placeholder="Enter a new password"
                value={state.password}
                onChange={handlers.handlePasswordChange}
                variant="outline"
                inputClassName="h-12 text-base sm:text-sm pr-12"
                error={state.passwordError}
              />
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
                onClick={handlers.handleToggleShowPassword}
              >
                {state.showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
                <span className="sr-only">
                  {state.showPassword ? 'Hide password' : 'Show password'}
                </span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="recovery-confirm-password" isRequired className="text-base">
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="recovery-confirm-password"
                type={state.showConfirmPassword ? 'text' : 'password'}
                required
                placeholder="Re-enter your password to confirm"
                value={state.confirmPassword}
                onChange={handlers.handleConfirmPasswordChange}
                variant="outline"
                inputClassName="h-12 text-base sm:text-sm pr-12"
                error={state.confirmPasswordError}
              />
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
                onClick={handlers.handleToggleShowConfirmPassword}
              >
                {state.showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
                <span className="sr-only">
                  {state.showConfirmPassword ? 'Hide password' : 'Show password'}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <PasswordRequirements
            password={state.password}
            confirmPassword={state.confirmPassword}
            includeMatchRequirement
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full h-12"
          isLoading={state.isLoading}
          loadingText="Resetting..."
          disabled={state.isContinueDisabled}
        >
          Reset Password
        </Button>

        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="link"
            className="p-0 h-auto text-muted-foreground hover:text-foreground gap-1"
            onClick={handlers.handleBack}
            disabled={state.isLoading}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back
          </Button>
        </div>
      </form>
    </>
  );
}
